import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Plus, Trash2, Edit, Save, Search, GraduationCap, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import { api, loadAuthToken } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";

const waLink = (s) => {
  let m = (s.mobile || "").replace(/[^0-9]/g, "");
  if (m.length === 10) m = "91" + m;
  if (m.length < 11) return null;
  const prog = [s.program, s.university].filter(Boolean).join(" · ");
  const msg = `नमस्ते ${s.name} जी 🙏\n${prog ? `आपके ${prog} के संबंध में` : "आपके आवेदन के संबंध में"} Ashish Point की ओर से संदेश।\n— Ashish Point`;
  return `https://wa.me/${m}?text=${encodeURIComponent(msg)}`;
};

const empty = () => ({ name: "", mobile: "", father_name: "", mother_name: "", dob: "", gender: "", program: "", university: "", roll_no: "", session: "", category: "", address: "", email: "", notes: "" });

const AdminStudentsPage = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [me, setMe] = useState(null);

  useEffect(() => {
    if (!loadAuthToken()) { navigate("/admin/login"); return; }
    try { setMe(JSON.parse(localStorage.getItem("ap_admin_user") || "null")); } catch {}
    load();
    // eslint-disable-next-line
  }, []);

  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); /* eslint-disable-next-line */ }, [q]);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/students", { params: q.trim() ? { q: q.trim() } : {} });
      setItems(r.data || []);
    } catch (e) { if (e?.response?.status === 401) navigate("/admin/login"); else toast.error("Failed"); }
    finally { setLoading(false); }
  };

  const save = async () => {
    if (!editing.name) { toast.error("Name required"); return; }
    setSaving(true);
    try {
      if (editing.id) {
        const { id, created_at, updated_at, ...body } = editing;
        await api.patch(`/admin/students/${editing.id}`, body);
        toast.success("Updated");
      } else { await api.post("/admin/students", editing); toast.success("Student added"); }
      setEditing(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const del = async (s) => {
    if (!window.confirm(`Delete ${s.name}?`)) return;
    try { await api.delete(`/admin/students/${s.id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };

  return (
    <div data-testid="admin-students-page" className="max-w-7xl mx-auto px-4 lg:px-8 py-8 lg:py-10">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <Button variant="ghost" size="sm" onClick={() => navigate("/admin")} className="mb-2" data-testid="students-back-btn"><ArrowLeft className="w-4 h-4 mr-1" /> Back</Button>
          <h1 className="text-2xl sm:text-3xl font-heading font-semibold text-slate-900 flex items-center gap-2"><GraduationCap className="w-6 h-6 text-teal-700" /> Student Corner</h1>
          <p className="text-sm text-slate-500 mt-1">All students onboarded — IGNOU, university admissions, scholarships.</p>
        </div>
        <Button onClick={() => setEditing(empty())} className="bg-teal-700 hover:bg-teal-800 text-white" data-testid="new-student-btn"><Plus className="w-4 h-4 mr-2" /> Add Student</Button>
      </div>

      <div className="rounded-lg bg-white border border-slate-200 p-3 mb-4 flex gap-2 items-center">
        <Search className="w-4 h-4 text-slate-400" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name, mobile, roll no or university…" data-testid="students-search" />
      </div>

      <div className="rounded-lg bg-white border border-slate-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-600 text-left">
            <tr>
              <th className="px-4 py-3 font-medium">Name</th>
              <th className="px-4 py-3 font-medium">Mobile</th>
              <th className="px-4 py-3 font-medium">Program</th>
              <th className="px-4 py-3 font-medium">University</th>
              <th className="px-4 py-3 font-medium">Roll No.</th>
              <th className="px-4 py-3 font-medium">Session</th>
              <th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan="7" className="px-4 py-10 text-center text-slate-500">Loading…</td></tr>}
            {!loading && items.length === 0 && <tr><td colSpan="7" className="px-4 py-10 text-center text-slate-500">No students yet.</td></tr>}
            {!loading && items.map((s) => (
              <tr key={s.id} className="border-t border-slate-100" data-testid={`student-row-${s.id}`}>
                <td className="px-4 py-3 font-medium">{s.name}</td>
                <td className="px-4 py-3 whitespace-nowrap">
                  {s.mobile || "—"}
                  {waLink(s) && (
                    <a href={waLink(s)} target="_blank" rel="noopener noreferrer" title="WhatsApp par message bhejein"
                      className="ml-2 inline-flex items-center gap-1 px-2 py-1 rounded-md bg-green-600 hover:bg-green-700 text-white text-xs" data-testid={`wa-student-${s.id}`}>
                      <MessageCircle className="w-3 h-3" /> WhatsApp
                    </a>
                  )}
                </td>
                <td className="px-4 py-3">{s.program || "—"}</td>
                <td className="px-4 py-3">{s.university || "—"}</td>
                <td className="px-4 py-3">{s.roll_no || "—"}</td>
                <td className="px-4 py-3">{s.session || "—"}</td>
                <td className="px-4 py-3 text-right whitespace-nowrap">
                  <button className="text-teal-700 hover:underline text-xs mr-3" onClick={() => setEditing(s)} data-testid={`edit-student-${s.id}`}><Edit className="w-3 h-3 inline mr-0.5" />Edit</button>
                  {me?.role === "super_admin" && <button className="text-red-600 hover:underline text-xs" onClick={() => del(s)} data-testid={`delete-student-${s.id}`}><Trash2 className="w-3 h-3 inline mr-0.5" />Delete</button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing && (
        <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="student-editor">
            <DialogHeader><DialogTitle>{editing.id ? "Edit Student" : "Add Student"}</DialogTitle></DialogHeader>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div className="col-span-2"><Label>Name *</Label><Input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} data-testid="student-name" /></div>
              <div><Label>Mobile</Label><Input value={editing.mobile || ""} onChange={(e) => setEditing({ ...editing, mobile: e.target.value })} /></div>
              <div><Label>Email</Label><Input value={editing.email || ""} onChange={(e) => setEditing({ ...editing, email: e.target.value })} /></div>
              <div><Label>Father's Name</Label><Input value={editing.father_name || ""} onChange={(e) => setEditing({ ...editing, father_name: e.target.value })} /></div>
              <div><Label>Mother's Name</Label><Input value={editing.mother_name || ""} onChange={(e) => setEditing({ ...editing, mother_name: e.target.value })} /></div>
              <div><Label>Date of Birth</Label><Input type="date" value={editing.dob || ""} onChange={(e) => setEditing({ ...editing, dob: e.target.value })} /></div>
              <div><Label>Gender</Label><Input value={editing.gender || ""} onChange={(e) => setEditing({ ...editing, gender: e.target.value })} /></div>
              <div><Label>Program</Label><Input value={editing.program || ""} onChange={(e) => setEditing({ ...editing, program: e.target.value })} placeholder="BA / MBA / IGNOU BAG" /></div>
              <div><Label>University / Board</Label><Input value={editing.university || ""} onChange={(e) => setEditing({ ...editing, university: e.target.value })} /></div>
              <div><Label>Roll / Enrol No.</Label><Input value={editing.roll_no || ""} onChange={(e) => setEditing({ ...editing, roll_no: e.target.value })} /></div>
              <div><Label>Session</Label><Input value={editing.session || ""} onChange={(e) => setEditing({ ...editing, session: e.target.value })} placeholder="Jan 2026" /></div>
              <div><Label>Category</Label><Input value={editing.category || ""} onChange={(e) => setEditing({ ...editing, category: e.target.value })} /></div>
              <div className="col-span-2"><Label>Address</Label><Textarea rows={2} value={editing.address || ""} onChange={(e) => setEditing({ ...editing, address: e.target.value })} /></div>
              <div className="col-span-2"><Label>Notes</Label><Textarea rows={2} value={editing.notes || ""} onChange={(e) => setEditing({ ...editing, notes: e.target.value })} /></div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
              <Button onClick={save} disabled={saving} className="bg-teal-700 hover:bg-teal-800 text-white" data-testid="save-student-btn"><Save className="w-4 h-4 mr-2" /> {saving ? "Saving…" : "Save"}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

export default AdminStudentsPage;
